/**
 * ReleaseOrderFormDialog — New / Edit for Sales Control › Release Order.
 * Converted 1:1 from Access `frmCtrlSalesQryROAdd_upgrade` (header form)
 * + `..._subForm` (releasable container datasheet picker).
 *
 * Header (Access 3-col grid, Left 2664/8880/15108):
 *   R1 ID (readonly) | Date | —
 *   R2 Release No. | Customer INV (combo) | INV-Client (readonly DLookUp buyer_name)
 *   R3 Release-NW | INV Total NW | INV Releasable NW    (all readonly Standard)
 *   R4 Release-GW | INV Total GW | INV Releasable GW
 *   R5 Release-Bag | INV Total BAG | INV Releasable Bag
 *   R6 Send to WH (combo) | Send to WH Name (readonly) | WH Storage (date)
 *   R7 Note (span 3, multiline)
 *
 * Snapshot totals (Access DSum): INV Total/Releasable = Σ over loaded containers;
 * Release NW/GW/Bag = Σ over the ticked picker rows (live equivalent of DSum on
 * v_release_order_scs). INV-Client + Send-to-WH Name = buyer_name / company_name.
 *
 * Cascade (Access InvChange): pick customer_invoice_no → Suggest_IndexName fills
 * release_no (FR{NN}/{invoice}) + load releasable containers + INV-Client.
 *
 * Container picker (Access subForm): tick a row → release = releasable (Access
 * checked_row_AfterUpdate). Enter Release NW → system computes Release GW/BAG
 * (Access nw_release_AfterUpdate: Round(nw/nw_cont * gw_cont|bag_cont), capped at
 * releasable) + auto-tick. Only ticked rows go into details[].
 *
 * Edit mode: customer_invoice_no + release_no DISABLED; hydrate header (+ readonly
 * snapshot) from GET /{id}/form; existing detail rows re-mark + fill the picker.
 *
 * Save validation (Access btnSave, verbatim EN): New duplicate release_no →
 * "Release No. is existed"; invoice empty → "Customer Invoice No. is required";
 * release_no empty → "Release No. is required"; date empty → "Release Date is
 * required".
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog,
  DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableFooter,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type ReleasableContainer,
  type RoLookupItem,
  type SaleReleaseOrderWrite,
  useCreateSaleReleaseOrderMutation,
  useUpdateSaleReleaseOrderMutation,
  useLazyGetSaleReleaseOrderFormQuery,
  useLazySuggestReleaseNoQuery,
  useLazyCheckReleaseNoQuery,
  useLazyGetReleaseOrderDetailsQuery,
  useLazyGetReleasableContainersQuery,
  useRoLookupCustomerInvoicesQuery,
  useRoLookupSendToWarehouseQuery,
} from '../../store/api/saleControlApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

type Mode = 'new' | 'edit'

interface Props {
  open: boolean
  mode: Mode
  roId?: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  customerInvoiceNo: string
  releaseNo: string
  releaseDate: string
  sendToWarehouse: string
  whRange: string
  note: string
  whStorageDeadline: string
  // Readonly snapshot (Access DLookUp / combo column 1) — not part of payload.
  invClient: string
  sendToWarehouseName: string
}
const EMPTY: Draft = {
  customerInvoiceNo: '', releaseNo: '', releaseDate: '', sendToWarehouse: '',
  whRange: '', note: '', whStorageDeadline: '', invClient: '', sendToWarehouseName: '',
}

/** Picker row = releasable container + editable release qty + tick. */
interface PickRow {
  key: string
  invoiceNo: string
  deliveryOrder: string
  containerNo: string
  nwCont: number | null
  gwCont: number | null
  bagCont: number | null
  nwReleasable: number | null
  gwReleasable: number | null
  bagReleasable: number | null
  checked: boolean
  nwRelease: string
  gwRelease: string
  bagRelease: string
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
/** Readonly snapshot field — grey bg to distinguish from editable (skill convert-form). */
const readonlyFieldSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5'),
  },
  '& .MuiInputBase-input': { color: (t) => t.palette.text.secondary, fontWeight: 600 },
}
// Header 3-col grid — Access Left 2664 / 8880 / 15108.
const grid3Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const numCellProps = { style: { textAlign: 'right' as const, fontSize: '0.75rem' }, inputMode: 'decimal' as const }
// Footer Total (Access TotalsRow=1, AggregateType=0 cho 9 cột số). Sticky đáy + opaque
// bg; color text.primary để KHÔNG bị variant="footer" đổi sang xám (convert-table §10.10b).
const footTotalSx: SxProps<Theme> = {
  fontWeight: 800, whiteSpace: 'nowrap', py: 0.5,
  bgcolor: 'background.paper', color: 'text.primary',
  borderTop: (t) => `2px solid ${t.palette.divider}`,
  position: 'sticky', bottom: 0, zIndex: 2,
  fontVariantNumeric: 'tabular-nums',
}

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
const numOr0 = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
/** Access Format="Standard" → `#,##0.00`, right-aligned. */
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const str = (v: unknown): string => (v == null ? '' : String(v))

function withCurrent(options: RoLookupItem[], value: string): RoLookupItem | null {
  if (!value) return null
  return options.find((o) => o.value === value) ?? { value, label: value }
}

/* ───────── Reusable lookup combo (flat "<CODE> — <NAME>", collapse when equal) ───────── */
function Combo(props: {
  label: string
  value: string
  options: RoLookupItem[]
  loading?: boolean
  required?: boolean
  disabled?: boolean
  labelOnly?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string) => void
  onBlur?: () => void
  error?: boolean
  helperText?: string
}) {
  const { label, value, options, loading, required, disabled, labelOnly, onInputChange, onPick, onBlur, error, helperText } = props
  const sel = withCurrent(options, value)
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size={SM}
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '')}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={(o) => (!o ? '' : labelOnly || o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => (
        <TextField {...params} label={required ? `${label} *` : label} onBlur={onBlur} error={error} helperText={helperText} />
      )}
    />
  )
}

export default function ReleaseOrderFormDialog({ open, mode, roId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [rows, setRows] = useState<PickRow[]>([])
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)
  const [validationError, setValidationError] = useState('')
  // Edit hydrate hits a view query via loadForm → show a loading state until the
  // API response lands, then render the fields (avoid a flash of empty inputs).
  const [formLoading, setFormLoading] = useState(false)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [loadForm] = useLazyGetSaleReleaseOrderFormQuery()
  const [suggestReleaseNo, suggestState] = useLazySuggestReleaseNoQuery()
  const [checkReleaseNo] = useLazyCheckReleaseNoQuery()
  const [getDetails] = useLazyGetReleaseOrderDetailsQuery()
  const [getContainers] = useLazyGetReleasableContainersQuery()
  const [createRo, { isLoading: creating }] = useCreateSaleReleaseOrderMutation()
  const [updateRo, { isLoading: updating }] = useUpdateSaleReleaseOrderMutation()
  const saving = creating || updating

  const [invQ, setInvQ] = useState('')
  const [whQ, setWhQ] = useState('')
  const skip = { skip: !open }
  const invoices = useRoLookupCustomerInvoicesQuery(invQ || undefined, skip)
  const warehouses = useRoLookupSendToWarehouseQuery(whQ || undefined, skip)

  const isEdit = mode === 'edit'

  /** Build picker rows from releasable containers; merge existing details (Edit). */
  const buildRows = (containers: ReleasableContainer[], details?: { containerNo: string | null; nwRelease: number | null; gwRelease: number | null; bagRelease: number | null }[]) => {
    const detMap = new Map<string, { nwRelease: number | null; gwRelease: number | null; bagRelease: number | null }>()
    for (const d of details ?? []) detMap.set(str(d.containerNo), { nwRelease: d.nwRelease, gwRelease: d.gwRelease, bagRelease: d.bagRelease })
    return containers.map<PickRow>((c) => {
      const det = detMap.get(str(c.container_no))
      return {
        key: str(c.container_no) || String(c.id),
        invoiceNo: str(c.invoice_no),
        deliveryOrder: str(c.delivery_order),
        containerNo: str(c.container_no),
        nwCont: c.nw_cont,
        gwCont: c.gw_cont,
        bagCont: c.bag_cont,
        nwReleasable: c.nw_releasable,
        gwReleasable: c.gw_releasable,
        bagReleasable: c.bag_releasable,
        checked: !!det,
        nwRelease: det ? fmtStd(det.nwRelease) : '',
        gwRelease: det ? fmtStd(det.gwRelease) : '',
        bagRelease: det ? fmtStd(det.bagRelease) : '',
      }
    })
  }

  const loadContainers = async (invoiceNo: string, releaseNoForDetails?: string) => {
    if (!invoiceNo) { setRows([]); return }
    const containers = await getContainers(invoiceNo).unwrap().catch(() => [] as ReleasableContainer[])
    let details: { containerNo: string | null; nwRelease: number | null; gwRelease: number | null; bagRelease: number | null }[] | undefined
    if (releaseNoForDetails) {
      const res = await getDetails({ releaseNo: releaseNoForDetails }).unwrap().catch(() => null)
      details = res?.items
    }
    setRows(buildRows(containers, details))
  }

  // Reset transient state on open
  useEffect(() => {
    if (!open) return
    setRegenNotice(null)
    setValidationError('')
    setPrecheckBusy(false)
    precheckBusyRef.current = false
  }, [open, mode, roId])

  // Hydrate on open. Deps limited to [open, mode, roId] (skill access-suggest #4).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    if (mode !== 'new') setFormLoading(true) // show loading immediately for Edit
    void (async () => {
      if (mode === 'new') {
        if (cancelled) return
        setFormLoading(false)
        setDraft({ ...EMPTY, releaseDate: dayjs().format('YYYY-MM-DD') })
        setRows([])
        return
      }
      if (!roId) { setFormLoading(false); return }
      const f = await loadForm(roId).unwrap().catch(() => null)
      if (cancelled) return
      if (!f) { setFormLoading(false); return }
      const g = (k: string) => str((f as Record<string, unknown>)[k])
      const invoiceNo = g('customerInvoiceNo')
      const releaseNo = g('releaseNo')
      setDraft({
        customerInvoiceNo: invoiceNo,
        releaseNo,
        releaseDate: g('releaseDate').slice(0, 10),
        sendToWarehouse: g('sendToWarehouse'),
        whRange: g('whRange'),
        note: g('note'),
        whStorageDeadline: g('whStorageDeadline').slice(0, 10),
        invClient: g('invClient'),
        sendToWarehouseName: g('sendToWarehouseName'),
      })
      await loadContainers(invoiceNo, releaseNo)
      setFormLoading(false)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, roId])

  // Dismiss regen alert when user edits release_no away from suggested value.
  useEffect(() => {
    if (regenNotice && draft.releaseNo !== regenNotice.to) setRegenNotice(null)
  }, [draft.releaseNo, regenNotice])

  /** Access InvChange: pick invoice → suggest release_no + reload containers + INV-Client. */
  const handleInvoicePick = async (invoiceNo: string) => {
    const opt = (invoices.data?.items ?? []).find((o) => o.value === invoiceNo)
    const buyerName = opt?.meta ? str((opt.meta as Record<string, unknown>).buyerName) : ''
    setDraft((p) => ({ ...p, customerInvoiceNo: invoiceNo, releaseNo: '', invClient: buyerName }))
    setRegenNotice(null)
    if (!invoiceNo) { setRows([]); return }
    const [suggest] = await Promise.all([
      suggestReleaseNo(invoiceNo, false).unwrap().catch(() => null),
      loadContainers(invoiceNo),
    ])
    const rn = (suggest?.releaseNo || '').trim()
    if (rn) setDraft((p) => ({ ...p, releaseNo: rn }))
  }

  /** Access send_to_warehouse_AfterUpdate: docs_dispatch = combo column(1) = company_name. */
  const handleWarehousePick = (code: string) => {
    const opt = (warehouses.data?.items ?? []).find((o) => o.value === code)
    const companyName = opt?.meta ? str((opt.meta as Record<string, unknown>).companyName) : ''
    setDraft((p) => ({ ...p, sendToWarehouse: code, sendToWarehouseName: companyName }))
  }

  /** Access btnSave errCheck 1 fast path: blur release_no → check duplicate → re-suggest. */
  const handleReleaseNoBlur = async () => {
    if (isEdit) return
    const val = draft.releaseNo.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await checkReleaseNo({ releaseNo: val }).unwrap()
      if (!check.exists) return
      const invoiceNo = draft.customerInvoiceNo.trim()
      if (!invoiceNo) return
      const next = await suggestReleaseNo(invoiceNo, false).unwrap()
      const newVal = (next.releaseNo || '').trim()
      if (newVal && newVal !== val) {
        setDraft((p) => ({ ...p, releaseNo: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE raises 422 at save if it truly duplicates — don't poison UX here.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  /* ───────── Picker row mutations ───────── */
  const setRow = (key: string, patch: Partial<PickRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  const toggleRow = (key: string, checked: boolean) =>
    setRows((prev) => prev.map((r) => {
      if (r.key !== key) return r
      // Access checked_row_AfterUpdate: tick → release = releasable; untick → clear.
      if (checked) return {
        ...r, checked: true,
        nwRelease: fmtStd(r.nwReleasable),
        gwRelease: fmtStd(r.gwReleasable),
        bagRelease: fmtStd(r.bagReleasable),
      }
      return { ...r, checked: false, nwRelease: '', gwRelease: '', bagRelease: '' }
    }))

  // Access nw_release_AfterUpdate: from Release NW compute GW/BAG (Round, capped at
  // releasable) + auto-tick when NW > 0. NW stays raw while typing; formatted on blur.
  const recalcNw = (r: PickRow, rawNw: string): Partial<PickRow> => {
    const patch: Partial<PickRow> = { nwRelease: rawNw }
    const nwRel = parseNum(rawNw)
    const nwCont = r.nwCont
    if (nwCont && nwCont !== 0 && nwRel != null) {
      let gw = Math.round((nwRel / nwCont) * numOr0(r.gwCont))
      let bag = Math.round((nwRel / nwCont) * numOr0(r.bagCont))
      if (r.gwReleasable != null && gw > r.gwReleasable) gw = r.gwReleasable
      if (r.bagReleasable != null && bag > r.bagReleasable) bag = r.bagReleasable
      patch.gwRelease = fmtStd(gw)
      patch.bagRelease = fmtStd(bag)
    } else {
      patch.gwRelease = ''
      patch.bagRelease = ''
    }
    if (nwRel != null && nwRel > 0) patch.checked = true
    return patch
  }
  const handleNwChange = (r: PickRow, rawNw: string) => setRow(r.key, recalcNw(r, rawNw))
  const handleNwBlur = (r: PickRow) => setRow(r.key, { ...recalcNw(r, r.nwRelease), nwRelease: fmtStd(r.nwRelease) })
  // Access: gw_release / bag_release cũng là ô EDIT (Enabled=True, không có AfterUpdate) →
  // nhập tay được, chỉ lưu raw + format on blur, KHÔNG cascade / auto-tick.
  const handleGwChange = (r: PickRow, v: string) => setRow(r.key, { gwRelease: v })
  const handleGwBlur = (r: PickRow) => setRow(r.key, { gwRelease: fmtStd(r.gwRelease) })
  const handleBagChange = (r: PickRow, v: string) => setRow(r.key, { bagRelease: v })
  const handleBagBlur = (r: PickRow) => setRow(r.key, { bagRelease: fmtStd(r.bagRelease) })

  // Access tick_all_Click: fill nw/gw/bag from releasable for all; clear when unticked.
  const handleTickAll = (checked: boolean) => {
    setRows((prev) => prev.map((r) => (checked
      ? { ...r, checked: true, nwRelease: fmtStd(r.nwReleasable), gwRelease: fmtStd(r.gwReleasable), bagRelease: fmtStd(r.bagReleasable) }
      : { ...r, checked: false, nwRelease: '', gwRelease: '', bagRelease: '' })))
  }

  // Access header DSum snapshot totals.
  const totals = useMemo(() => {
    const t = { invNw: 0, invGw: 0, invBag: 0, relNw: 0, relGw: 0, relBag: 0, rNw: 0, rGw: 0, rBag: 0 }
    for (const r of rows) {
      t.invNw += numOr0(r.nwCont); t.invGw += numOr0(r.gwCont); t.invBag += numOr0(r.bagCont)
      t.relNw += numOr0(r.nwReleasable); t.relGw += numOr0(r.gwReleasable); t.relBag += numOr0(r.bagReleasable)
      if (r.checked) {
        t.rNw += parseNum(r.nwRelease) ?? 0
        t.rGw += parseNum(r.gwRelease) ?? 0
        t.rBag += parseNum(r.bagRelease) ?? 0
      }
    }
    return t
  }, [rows])

  // Tick-all header derive từ rows → Edit mode load full ticked thì header tự tick;
  // partial → indeterminate. (Không giữ state riêng để tránh lệch.)
  const allTicked = rows.length > 0 && rows.every((r) => r.checked)
  const someTicked = rows.some((r) => r.checked)

  const invoiceEmpty = !draft.customerInvoiceNo.trim()
  const releaseNoEmpty = !draft.releaseNo.trim()
  const dateEmpty = !draft.releaseDate.trim()
  const canSave = canEdit && !invoiceEmpty && !releaseNoEmpty && !dateEmpty && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (precheckBusyRef.current) return // race guard (blur + Save same tick)
    setValidationError('')
    // Access btnSave errCheck 2/3/4 (verbatim EN)
    if (invoiceEmpty) { setValidationError('Customer Invoice No. is required'); return }
    if (releaseNoEmpty) { setValidationError('Release No. is required'); return }
    if (dateEmpty) { setValidationError('Release Date is required'); return }
    if (!canSave) return

    const payload: SaleReleaseOrderWrite = {
      customerInvoiceNo: draft.customerInvoiceNo || null,
      releaseNo: draft.releaseNo || null,
      releaseDate: draft.releaseDate || null,
      sendToWarehouse: draft.sendToWarehouse || null,
      whStorageDeadline: draft.whStorageDeadline || null,
      whRange: draft.whRange || null,
      note: draft.note || null,
      details: rows.filter((r) => r.checked).map((r) => ({
        invoiceNo: r.invoiceNo || null,
        deliveryOrder: r.deliveryOrder || null,
        containerNo: r.containerNo || null,
        nwRelease: parseNum(r.nwRelease),
        gwRelease: parseNum(r.gwRelease),
        bagRelease: parseNum(r.bagRelease),
      })),
    }
    try {
      if (isEdit && roId) {
        await updateRo({ id: roId, body: payload }).unwrap()
        snack.success(`Release Order ${draft.releaseNo} updated`)
      } else {
        await createRo(payload).unwrap()
        snack.success(`Release Order ${draft.releaseNo} added`)
      }
      onSaved()
      onClose()
    } catch (err) {
      const msg = getErrorMessage(err, 'Save release order failed')
      // Access btnSave errCheck 1: New + duplicate release_no → BE 422.
      if (!isEdit && /exist|duplicate/i.test(msg)) { setValidationError('Release No. is existed'); return }
      snack.error(msg)
    }
  }

  const title = isEdit ? 'Edit' : 'New'

  const dateField = (key: keyof Draft, label: string, required?: boolean) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, required, InputLabelProps: { shrink: true } } }}
    />
  )
  /** Readonly snapshot text field (Access Enabled=NotDefault). `num` → right-align + Standard. */
  const roField = (label: string, value: string, num?: boolean) => (
    <TextField
      label={label} size={SM} fullWidth value={value}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
      inputProps={{ tabIndex: -1, ...(num ? { style: { textAlign: 'right' } } : {}) }}
      sx={readonlyFieldSx}
    />
  )

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>{title} — Release Order</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {formLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
        <>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit release orders.</Alert>}
        {regenNotice && (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Release No. <b>{regenNotice.from}</b> was just created — suggested new code <b>{regenNotice.to}</b>.
          </Alert>
        )}
        {validationError && <Alert severity="error" sx={{ mb: 1.25, py: 0.25 }} onClose={() => setValidationError('')}>{validationError}</Alert>}

        {/* ── Header ── */}
        <Typography sx={sectionTitleSx}>Release order information</Typography>
        <Box sx={grid3Sx}>
          {/* R1 */}
          {roField('ID', isEdit && roId ? String(roId) : '')}
          {dateField('releaseDate', 'Date', true)}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
          {/* R2 */}
          <TextField
            label="Release No. *" size={SM} fullWidth value={draft.releaseNo}
            disabled={isEdit}
            onChange={(e) => set('releaseNo', e.target.value)}
            onBlur={() => void handleReleaseNoBlur()}
            error={releaseNoEmpty}
            InputLabelProps={{ shrink: true }}
            helperText={
              isEdit ? 'Release No. cannot be changed after creation.'
                : precheckBusy ? 'Verifying Release No.…'
                : suggestState.isFetching ? 'Suggesting next code…'
                : 'Auto-suggested from Customer INV; edit if needed.'
            }
          />
          <Combo label="Customer INV" value={draft.customerInvoiceNo} required disabled={isEdit} labelOnly
            options={invoices.data?.items ?? []} loading={invoices.isFetching} onInputChange={setInvQ}
            error={invoiceEmpty} onPick={(v) => void handleInvoicePick(v)} />
          {roField('INV - Client', draft.invClient)}
          {/* R3 */}
          {roField('Release - NW', fmtStd(totals.rNw), true)}
          {roField('INV - Total NW', fmtStd(totals.invNw), true)}
          {roField('INV - Releasable NW', fmtStd(totals.relNw), true)}
          {/* R4 */}
          {roField('Release - GW', fmtStd(totals.rGw), true)}
          {roField('INV - Total GW', fmtStd(totals.invGw), true)}
          {roField('INV - Releasable GW', fmtStd(totals.relGw), true)}
          {/* R5 */}
          {roField('Release - Bag', fmtStd(totals.rBag), true)}
          {roField('INV - Total BAG', fmtStd(totals.invBag), true)}
          {roField('INV - Releasable Bag', fmtStd(totals.relBag), true)}
          {/* R6 */}
          <Combo label="Send to WH" value={draft.sendToWarehouse}
            options={warehouses.data?.items ?? []} loading={warehouses.isFetching} onInputChange={setWhQ}
            onPick={handleWarehousePick} />
          {roField('Send to WH Name', draft.sendToWarehouseName)}
          {dateField('whStorageDeadline', 'WH Storage')}
          {/* R7 */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <TextField label="Note" size={SM} fullWidth multiline minRows={2} value={draft.note}
              onChange={(e) => set('note', e.target.value)} />
          </Box>
        </Box>

        {/* ── Release details (Access subForm container picker) ── */}
        <Typography sx={sectionTitleSx}>Release details</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Tick the rows to release. Enter Release NW — the system computes Release GW and Release BAG.
        </Typography>
        {invoiceEmpty ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Choose a Customer INV to load releasable containers.
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No releasable containers.</Typography>
        ) : (
          /* Style theo /convert-table (canonical ContractListView): app-dataframe (card)
             > app-dataframe-toolbar (count) > scroll box > MUI Table với header
             app-table-header-cell (gradient vàng) + row MUI mặc định (hover/selected).
             KHÔNG app-table-wrap (đó là style legacy sọc vàng, không phải canonical).
             In-dialog picker → cột EDIT giữ TextField; KHÔNG filter bar/pagination/column menu. */
          <Box sx={{ '& > .app-dataframe': { height: 'auto', minHeight: 0 } }}>
            <Box className="app-dataframe">
              <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'primary.dark' }}>
                  {rows.length} container{rows.length > 1 ? 's' : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary' }}>
                  · {rows.filter((r) => r.checked).length} selected
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 300, overflow: 'auto', minHeight: 0 }}>
                <Table size="small" stickyHeader sx={{ minWidth: 1180 }}>
                  <TableHead>
                    {/* Caption + thứ tự 1:1 Access subForm datasheet ('(EDIT)' = cột nhập tay). */}
                    <TableRow>
                      <TableCell className="app-table-header-cell" padding="checkbox" align="center" title="To be Edited">
                        <Checkbox size="small" checked={allTicked} indeterminate={someTicked && !allTicked} disabled={!canEdit}
                          onChange={(e) => handleTickAll(e.target.checked)} sx={{ p: 0 }} title="Tick all" />
                      </TableCell>
                      <TableCell className="app-table-header-cell">DeliveryOrder</TableCell>
                      <TableCell className="app-table-header-cell">ContainerNo</TableCell>
                      <TableCell className="app-table-header-cell" align="right">ReleasableNW</TableCell>
                      <TableCell className="app-table-header-cell" align="right">ReleaseNW (EDIT)</TableCell>
                      <TableCell className="app-table-header-cell" align="right">ReleasableGW</TableCell>
                      <TableCell className="app-table-header-cell" align="right">ReleaseGW (EDIT)</TableCell>
                      <TableCell className="app-table-header-cell" align="right">ReleasableBAG</TableCell>
                      <TableCell className="app-table-header-cell" align="right">ReleaseBAG (EDIT)</TableCell>
                      <TableCell className="app-table-header-cell" align="right">TotalNW</TableCell>
                      <TableCell className="app-table-header-cell" align="right">TotalGW</TableCell>
                      <TableCell className="app-table-header-cell" align="right">TotalBAG</TableCell>
                      <TableCell className="app-table-header-cell">InvoiceNo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.key} hover selected={r.checked}>
                        <TableCell padding="checkbox" align="center">
                          <Checkbox size="small" checked={r.checked} disabled={!canEdit}
                            onChange={(e) => toggleRow(r.key, e.target.checked)} sx={{ p: 0 }} />
                        </TableCell>
                        <TableCell><Box className="app-table-cell-text" title={r.deliveryOrder ?? ''}>{r.deliveryOrder}</Box></TableCell>
                        <TableCell><Box className="app-table-cell-text" title={r.containerNo ?? ''}>{r.containerNo}</Box></TableCell>
                        <TableCell align="right">{fmtStd(r.nwReleasable)}</TableCell>
                        <TableCell align="right">
                          <TextField variant="standard" fullWidth value={r.nwRelease} disabled={!canEdit}
                            onChange={(e) => handleNwChange(r, e.target.value)}
                            onBlur={() => handleNwBlur(r)}
                            inputProps={numCellProps} />
                        </TableCell>
                        <TableCell align="right">{fmtStd(r.gwReleasable)}</TableCell>
                        {/* Release GW — Access ô EDIT: auto-tính từ Release NW nhưng nhập tay được. */}
                        <TableCell align="right">
                          <TextField variant="standard" fullWidth value={r.gwRelease} disabled={!canEdit}
                            onChange={(e) => handleGwChange(r, e.target.value)}
                            onBlur={() => handleGwBlur(r)}
                            inputProps={numCellProps} />
                        </TableCell>
                        <TableCell align="right">{fmtStd(r.bagReleasable)}</TableCell>
                        {/* Release BAG — Access ô EDIT: auto-tính từ Release NW nhưng nhập tay được. */}
                        <TableCell align="right">
                          <TextField variant="standard" fullWidth value={r.bagRelease} disabled={!canEdit}
                            onChange={(e) => handleBagChange(r, e.target.value)}
                            onBlur={() => handleBagBlur(r)}
                            inputProps={numCellProps} />
                        </TableCell>
                        <TableCell align="right">{fmtStd(r.nwCont)}</TableCell>
                        <TableCell align="right">{fmtStd(r.gwCont)}</TableCell>
                        <TableCell align="right">{fmtStd(r.bagCont)}</TableCell>
                        <TableCell><Box className="app-table-cell-text" title={r.invoiceNo ?? ''}>{r.invoiceNo}</Box></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  {/* Total row — Access TotalsRow=1: Sum 9 cột số (AggregateType=0). */}
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3} sx={footTotalSx}>Total</TableCell>
                      <TableCell align="right" sx={footTotalSx}>{fmtStd(totals.relNw)}</TableCell>
                      <TableCell align="right" sx={footTotalSx}>{fmtStd(totals.rNw)}</TableCell>
                      <TableCell align="right" sx={footTotalSx}>{fmtStd(totals.relGw)}</TableCell>
                      <TableCell align="right" sx={footTotalSx}>{fmtStd(totals.rGw)}</TableCell>
                      <TableCell align="right" sx={footTotalSx}>{fmtStd(totals.relBag)}</TableCell>
                      <TableCell align="right" sx={footTotalSx}>{fmtStd(totals.rBag)}</TableCell>
                      <TableCell align="right" sx={footTotalSx}>{fmtStd(totals.invNw)}</TableCell>
                      <TableCell align="right" sx={footTotalSx}>{fmtStd(totals.invGw)}</TableCell>
                      <TableCell align="right" sx={footTotalSx}>{fmtStd(totals.invBag)}</TableCell>
                      <TableCell sx={footTotalSx} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </Box>
            </Box>
          </Box>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
